import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const CHAPTERS = [
  {
    n: "01",
    title: "Diagnostics first, tune second.",
    body:
      "Every car meets the OBD scanner before it meets our map. If a sensor is lying, no tune will save it. We fix the underlying fault, log the baseline, then we calibrate.",
    image:
      "https://images.unsplash.com/photo-1615906655593-ad0386982a0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxtZWNoYW5pYyUyMHdvcmtpbmclMjBvbiUyMGNhciUyMGVuZ2luZSUyMGRhcmt8ZW58MHx8fHwxNzg0MTg1NTM0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    n: "02",
    title: "Custom maps only. No cloud files.",
    body:
      "We do not resell generic files. Every calibration is written to your VIN — factoring altitude, fuel quality, and how you actually drive between the A52 and the M1.",
    image:
      "https://images.unsplash.com/photo-1782404183373-aefa860ae6b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxjYXIlMjBlbmdpbmUlMjBkZXRhaWwlMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc4NDE4NTUzNHww&ixlib=rb-4.1.0&q=85",
  },
  {
    n: "03",
    title: "Road-verified. Documented. Warrantied.",
    body:
      "We log baseline and final runs live on the road with pro-grade data-loggers. You leave with the delta report, the original file archived, and a 30-day peace-of-mind warranty on the calibration.",
    image:
      "https://images.unsplash.com/photo-1705601009931-a1a398654e65?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjANjA1NTJ8MHwxfHNlYXJjaHwyfHxjYXIlMjBlbmdpbmUlMjBkZXRhaWwlMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc4NDE4NTUzNHww&ixlib=rb-4.1.0&q=85",
  },
];

function Chapter({ chap, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <div
      ref={ref}
      className="grid grid-cols-12 gap-4 md:gap-6 py-12 md:py-16 border-t border-white/10 first:border-t-0"
      data-testid={`manifesto-chapter-${chap.n}`}
    >
      {/* Number */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
        className="col-span-3 md:col-span-2"
      >
        <div className="font-display text-6xl md:text-8xl lg:text-9xl text-volt leading-none">
          {chap.n}
        </div>
      </motion.div>

      {/* Text */}
      <div className={`col-span-9 md:col-span-4 ${index % 2 === 1 ? "md:col-start-3" : ""}`}>
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight mb-6"
        >
          {chap.title}
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="font-body text-white/60 text-base md:text-lg leading-relaxed max-w-md"
        >
          {chap.body}
        </motion.p>
      </div>

      {/* Image */}
      <div className={`col-span-12 md:col-span-6 relative overflow-hidden clipped-frame h-[300px] md:h-[420px] ${index % 2 === 1 ? "md:col-start-7" : ""}`}>
        <motion.img
          style={{ y: imgY }}
          src={chap.image}
          alt={chap.title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-widest text-white/70">
          Chapter {chap.n}
        </div>
      </div>
    </div>
  );
}

export default function Manifesto() {
  return (
    <section
      id="manifesto"
      className="relative bg-carbon py-24 md:py-32 px-6 md:px-10 border-t border-white/10"
      data-testid="manifesto-section"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="grid grid-cols-12 gap-6 mb-16">
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-volt">
              ◉ Manifesto
            </p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-display uppercase text-4xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tighter">
              Three chapters. <br />
              <span className="text-white/40">Zero shortcuts.</span>
            </h2>
          </div>
        </div>

        {CHAPTERS.map((c, i) => (
          <Chapter chap={c} index={i} key={c.n} />
        ))}
      </div>
    </section>
  );
}
